import React from 'react';
import {
  Box,
  TextField,
  Grid,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  MenuItem
} from '@mui/material';
import type { Person, Gender } from '../../../../types';

interface BasicInfoFormProps {
  data: Partial<Person>;
  errors: Record<string, string>;
  onChange: (field: string, value: any) => void;
  disabled?: boolean;
}

/**
 * BasicInfoForm - Edit basic identity information
 *
 * Fields:
 * - First Name (required)
 * - Middle Name
 * - Last Name (required)
 * - Suffix
 * - Maiden Name
 * - Nicknames (comma-separated)
 * - Gender (required)
 */
export const BasicInfoForm: React.FC<BasicInfoFormProps> = ({
  data,
  errors,
  onChange,
  disabled = false
}) => {
  const suffixOptions = ['', 'Jr.', 'Sr.', 'II', 'III', 'IV', 'V'];

  return (
    <Box sx={{ pt: 2 }}>
      <Grid container spacing={3}>
        {/* First Name */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="First Name"
            value={data.firstName || ''}
            onChange={(e) => onChange('firstName', e.target.value)}
            error={!!errors.firstName}
            helperText={errors.firstName}
            required
            disabled={disabled}
          />
        </Grid>

        {/* Middle Name */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Middle Name"
            value={data.middleName || ''}
            onChange={(e) => onChange('middleName', e.target.value)}
            error={!!errors.middleName}
            helperText={errors.middleName}
            disabled={disabled}
          />
        </Grid>

        {/* Last Name */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Last Name"
            value={data.lastName || ''}
            onChange={(e) => onChange('lastName', e.target.value)}
            error={!!errors.lastName}
            helperText={errors.lastName}
            required
            disabled={disabled}
          />
        </Grid>

        {/* Suffix */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            select
            label="Suffix"
            value={data.suffix || ''}
            onChange={(e) => onChange('suffix', e.target.value)}
            error={!!errors.suffix}
            helperText={errors.suffix}
            disabled={disabled}
          >
            {suffixOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option || 'None'}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        {/* Maiden Name */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Maiden Name"
            value={data.maidenName || ''}
            onChange={(e) => onChange('maidenName', e.target.value)}
            error={!!errors.maidenName}
            helperText={errors.maidenName}
            disabled={disabled}
          />
        </Grid>

        {/* Nicknames */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Nicknames"
            value={
              Array.isArray(data.nicknames)
                ? data.nicknames.join(', ')
                : data.nicknames || ''
            }
            onChange={(e) => onChange('nicknames', e.target.value)}
            onBlur={(e) => {
              // Process nicknames on blur instead of onChange to allow typing spaces/commas
              const processed = e.target.value.split(',').map(n => n.trim()).filter(Boolean);
              onChange('nicknames', processed);
            }}
            error={!!errors.nicknames}
            helperText={errors.nicknames || 'Separate multiple nicknames with commas'}
            disabled={disabled}
          />
        </Grid>

        {/* Gender */}
        <Grid item xs={12}>
          <FormControl component="fieldset" error={!!errors.gender} disabled={disabled}>
            <FormLabel component="legend" required>Gender</FormLabel>
            <RadioGroup
              row
              value={data.gender || 'male'}
              onChange={(e) => onChange('gender', e.target.value as Gender)}
            >
              <FormControlLabel
                value="male"
                control={<Radio />}
                label="Male"
              />
              <FormControlLabel
                value="female"
                control={<Radio />}
                label="Female"
              />
              <FormControlLabel
                value="other"
                control={<Radio />}
                label="Other"
              />
            </RadioGroup>
            {errors.gender && (
              <Box component="span" sx={{ color: 'error.main', fontSize: '0.75rem', mt: 0.5 }}>
                {errors.gender}
              </Box>
            )}
          </FormControl>
        </Grid>
      </Grid>
    </Box>
  );
};
